import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectUserData } from '../../redux/user/user.selectors';
import { mainGrid } from '../../style/grid.module.scss';
import {
    container,
    inner,
    text,
    actions,
    installButton,
    closeButton,
} from './pwa-install-bar.module.scss';
import CloseIcon from '../../assets/images/svg/icon-close.svg';

import useHasMounted from '../../hooks/useHasMounted';
import { getDeferredPrompt, clearDeferredPrompt } from '../../utills/pwa-install-prompt';

const SESSION_KEY = 'pwa-banner-dismissed';

interface IPwaInstallBarProps {
    className?: string;
}

const PwaInstallBar: React.FC<IPwaInstallBarProps> = ({ className = '' }) => {
    const hasMounted = useHasMounted();
    const isLogged = useSelector(selectUserData) !== null;
    const [isVisible, setIsVisible] = useState(false);
    const [isIos, setIsIos] = useState(false);

    const handleInstall = useCallback(async () => {
        const prompt = getDeferredPrompt();
        if (!prompt) return;
        await prompt.prompt();
        const { outcome } = await prompt.userChoice;
        if (outcome === 'accepted') {
            clearDeferredPrompt();
            sessionStorage.setItem(SESSION_KEY, '1');
            setIsVisible(false);
        }
    }, []);

    const handleClose = useCallback(() => {
        sessionStorage.setItem(SESSION_KEY, '1');
        setIsVisible(false);
    }, []);

    useEffect(() => {
        if (window.matchMedia('(display-mode: standalone)').matches) return;
        if (sessionStorage.getItem(SESSION_KEY)) return;

        const userAgent = navigator.userAgent;
        const iosDevice = /iphone|ipad|ipod/i.test(userAgent);
        const safariOnly =
            /safari/i.test(userAgent) && !/chrome|chromium|crios|fxios|opios/i.test(userAgent);

        if (iosDevice && safariOnly) {
            setIsIos(true);
            setIsVisible(true);
            return;
        }

        if (getDeferredPrompt()) {
            setIsVisible(true);
        }
        const handlePWAInstallable = () => setIsVisible(true);
        window.addEventListener('pwa-installable', handlePWAInstallable);
        return () => {
            window.removeEventListener('pwa-installable', handlePWAInstallable);
        };
    }, []);

    if (!hasMounted || !isVisible || !isLogged) return null;

    return (
        <div className={`${container} ${mainGrid} ${className}`}>
            <div className={inner}>
                {isIos ? (
                    <>
                        <span className={text}>
                            <strong>Pobierz aplikację</strong>: naciśnij w prawym dolnym rogu
                            wielokropek, następnie udostępnij i dodaj do ekranu głównego.
                        </span>
                        <div className={actions}>
                            <button
                                className={closeButton}
                                onClick={handleClose}
                                aria-label="Zamknij"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <span className={text}>Zainstaluj aplikację na swoim urządzeniu</span>
                        <div className={actions}>
                            <button className={installButton} onClick={handleInstall}>
                                Zainstaluj
                            </button>
                            <button
                                className={closeButton}
                                onClick={handleClose}
                                aria-label="Zamknij"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PwaInstallBar;
